/**
 * Точка доставки, совпадающая с координатами филиала, — не адрес клиента.
 *
 * Так выглядит утечка стартовой точки карты: окно выбора адреса открывается на
 * филиале, и его координаты попадали в выбранный адрес, хранилище и заказ (заказ
 * #8300: строка хутора вне зон, точка — филиала «Широкая 32»; точка филиала лежит в
 * его же зоне, и сервер честно принимал такой заказ). Клиент не живёт на пине
 * магазина, поэтому такие координаты отбрасываются везде: при записи адреса на
 * карте, при восстановлении из хранилища и перед отправкой заказа. То же правило
 * стоит на сервере (DeliveryPointGuardService).
 */

export interface BranchPointLike {
  latitude?: string | number | null
  longitude?: string | number | null
}

/** Допуск сравнения координат: 1e-6° ≈ 0,1 м — та же точка с запасом на округление. */
export const SAME_POINT_EPSILON = 1e-6

const toNumber = (value: string | number | null | undefined): number =>
  value == null ? Number.NaN : Number(value)

/** Одна и та же точка (с допуском {@link SAME_POINT_EPSILON}). */
export function isSamePoint(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
  epsilon: number = SAME_POINT_EPSILON,
): boolean {
  if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return false
  return Math.abs(aLat - bLat) < epsilon && Math.abs(aLng - bLng) < epsilon
}

/** Филиал, на координатах которого стоит точка, или null. */
export function findBranchAtPoint<T extends BranchPointLike>(
  latitude: string | number | null | undefined,
  longitude: string | number | null | undefined,
  branches: readonly T[] | null | undefined,
): T | null {
  const lat = toNumber(latitude)
  const lng = toNumber(longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !branches?.length) return null
  return (
    branches.find((b) => isSamePoint(lat, lng, toNumber(b.latitude), toNumber(b.longitude))) ?? null
  )
}

/** Точка совпадает с координатами какого-либо филиала. */
export function isAtBranchPoint(
  latitude: string | number | null | undefined,
  longitude: string | number | null | undefined,
  branches: readonly BranchPointLike[] | null | undefined,
): boolean {
  return findBranchAtPoint(latitude, longitude, branches) !== null
}
